import PropTypes from 'prop-types';

import {Flex, Box, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {ShopTheLookEmbedProvider} from '@amblique/king-living/app/contentful/models/shop-the-look-embed-context';

/**
 * SplitPanelDoubleImageBlock (Double image carousel, CF-07)
 *
 * Layout by mediaPosition: "left" = primary (large) image left, secondary (text + small image) right;
 * "right" = primary right, secondary left. Mobile: column, large image first then row (text + small image).
 * Figma: Right Desktop 3132-2147, Mobile 3132-6345; Left Desktop 3132-2140, Mobile 3132-6338 (KL-Designs).
 *
 * @param {Object} props - Component props
 * @param {Contentful.SplitPanelDoubleImageBlock} props.component - Component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const SplitPanelDoubleImageBlock = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {mediaPosition, primaryImage, contentCollection} = component;

  // Normalize mediaPosition to lowercase for variant matching
  const normalizedPosition = mediaPosition?.toLowerCase() || 'left';

  const styles = useMultiStyleConfig('contentful/split-panel-double-image-block', {
    component,
    isComplete,
    isFetching,
    variant: normalizedPosition,
  });

  // Need at least primary image or secondary content
  if (!primaryImage && !contentCollection?.items?.length) {
    return null;
  }

  // Filter valid secondary content items
  const secondaryItems = contentCollection?.items?.filter(
    (item) => item != null && typeof item === 'object' && item.__typename && item.sys?.id
  ) || [];

  return (
    <ShopTheLookEmbedProvider>
      <Flex {...styles.container} {...props}>
        {/* Primary Image Region */}
        {primaryImage && (
          <Box {...styles.primaryRegion}>
            <Component
              component={primaryImage}
              isComplete={isComplete}
              isFetching={isFetching}
            />
          </Box>
        )}

        {/* Secondary Content Region */}
        {secondaryItems.length > 0 && (
          <Box {...styles.secondaryRegion}>
            {secondaryItems.map((item) => (
              <Component
                key={item.sys.id}
                component={item}
                isComplete={isComplete}
                isFetching={isFetching}
              />
            ))}
          </Box>
        )}
      </Flex>
    </ShopTheLookEmbedProvider>
  );
};

SplitPanelDoubleImageBlock.displayName = 'SplitPanelDoubleImageBlock';
SplitPanelDoubleImageBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
